import type { ReactNode } from "react"
import React from "react"

import type { BoxProps } from "@chakra-ui/react"
import { Box, Center, CloseButton, Stack, Text } from "@chakra-ui/react"

import type { EnumDictionary } from "@supliful/domain"

import { ErrorIcon } from "./icons/ErrorIcon"
import { SuccessIcon } from "./icons/SuccessIcon"

export enum ToastVariant {
  SUCCESS = "Success",
  WARNING = "Warning",
  ERROR = "Error",
}

type Props = {
  message: ReactNode
  variant?: ToastVariant
  onClose: () => void
} & BoxProps

const backgroundColor: EnumDictionary<ToastVariant, string> = {
  [ToastVariant.SUCCESS]: "main.700",
  [ToastVariant.WARNING]: "neutrals.800",
  [ToastVariant.ERROR]: "red.300",
}

const textColor: EnumDictionary<ToastVariant, string> = {
  [ToastVariant.SUCCESS]: "white",
  [ToastVariant.WARNING]: "white",
  [ToastVariant.ERROR]: "white",
}

export const Toast = ({
  message,
  onClose,
  variant = ToastVariant.SUCCESS,
  ...props
}: Props) => {
  return (
    <Box
      position="relative"
      color={textColor[variant]}
      fontWeight={600}
      fontSize={14}
      bg={backgroundColor[variant]}
      boxShadow="0px 0px 30px rgba(0, 0, 0, 0.17)"
      borderRadius="5px"
      w="fit-content"
      minW="300px"
    >
      <Box position="absolute" right={0}>
        <CloseButton size="sm" onClick={onClose} />
      </Box>

      <Stack
        direction="column"
        align="center"
        pl={6}
        pr={8}
        pt={4}
        pb={3}
        {...props}
      >
        <Center>
          {(variant === ToastVariant.SUCCESS ||
            variant === ToastVariant.WARNING) && (
            <Box
              as={SuccessIcon}
              boxSize="24px"
              fill="white"
              color={backgroundColor[variant]}
            />
          )}

          {variant === ToastVariant.ERROR && (
            <Box
              as={ErrorIcon}
              boxSize="24px"
              fill="white"
              color={backgroundColor[variant]}
            />
          )}
          <Text pl={2}>{message}</Text>
        </Center>

        <Center>{props.children}</Center>
      </Stack>
    </Box>
  )
}
